"use client";

import React from "react";
import { ArrowRight, Calendar, MapPin, Gauge } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const pricingFactors = [{
  icon: Gauge,
  title: "Trailer Size",
  description: "Available options include 6×8, 7×12, and 7×16 refrigerated and freezer trailers, plus a 53-foot reefer trailer. Availability and suitability are confirmed with each quote."
}, {
  icon: Calendar,
  title: "Rental Dates & Delivery Location",
  description: "Requested delivery and pickup dates and the delivery location are included when preparing each quote."
}, {
  icon: MapPin,
  title: "Power & Optional Add-Ons",
  description: "Generator and shelving options may be available for eligible smaller trailers. Power, fuel, and optional-equipment requirements for the 53-foot reefer are confirmed individually."
}];
export default function Quote() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="call-to-action" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-20 lg:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative rounded-2xl bg-card border border-border p-5 sm:p-10 lg:p-14 shadow-lg">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12 items-center">
            {/* Main Content Column */}
            <div className="lg:col-span-7 space-y-6 min-w-0">
              <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 border border-accent/40 text-accent-foreground text-xs font-semibold tracking-wide uppercase max-w-full">
                <span className="h-2 w-2 rounded-full bg-primary animate-pulse shrink-0" />
                <span className="truncate">Dispatch availability</span>
              </motion.div>

              <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default tracking-tight text-card-foreground text-balance break-words">
                Get an accurate quote for your rental dates
              </motion.h2>

              <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl font-default break-words">
                Rental pricing is provided with each quote based on trailer size, rental dates, delivery and pickup location, site access, power needs, optional equipment, and trailer availability. Requirements for the 53-foot reefer are confirmed individually.
              </motion.p>

              {/* Dual Action CTA Dispatch Cluster */}
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2 w-full">
                <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 py-3.5 h-auto text-base rounded-md shadow-sm transition-[color,background-color,border-color,box-shadow]">
                  <Link to="/ContactRequestAQuotePage" className="inline-flex items-center justify-center gap-2">
                    <span>Request a Quote</span>
                    <ArrowRight className="size-4 shrink-0" />
                  </Link>
                </Button>

                <a href="tel:2135218821" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-medium text-muted-foreground bg-muted/50 rounded-md border border-border w-full sm:w-auto transition-colors hover:bg-muted">
                  <span>213-521-8821</span>
                </a>
              </motion.div>
            </div>

            {/* Spec & Pricing Factors Column */}
            <div className="lg:col-span-5 min-w-0 w-full">
              <motion.div variants={itemVariants} className="rounded-xl bg-muted/40 border border-border p-4 sm:p-6 space-y-4 min-w-0">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground font-default">
                  Quote Calculation Factors
                </h3>

                <div className="space-y-4 pt-1">
                  {pricingFactors.map((factor, i) => {
                  const IconComponent = factor.icon;
                  return <div key={factor.title} data-index={i} className="flex items-start gap-3.5 p-3 rounded-lg bg-card border border-border/80 min-w-0">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary mt-0.5">
                          <IconComponent className="size-4" />
                        </div>
                        <div className="space-y-1 min-w-0 flex-1">
                          <p className="text-sm font-semibold text-card-foreground font-default break-words">
                            {factor.title}
                          </p>
                          <p className="text-xs text-muted-foreground leading-normal font-default break-words">
                            {factor.description}
                          </p>
                        </div>
                      </div>;
                })}
                </div>

                <div className="pt-2 border-t border-border/80 text-xs text-muted-foreground break-words">
                  Serving Southern California and our listed Las Vegas-area communities.
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}